import styled from "styled-components";
import { FC, useState, useMemo, useCallback, MouseEventHandler } from "react";

import { primaryActive } from "../../../typography/alpha";
import { Tooltip } from "../../Tooltip";
import { TooltipPlacement } from "../../Tooltip/tooltip.types";
import { Data } from "./Data";
import { Graph } from "./Graph";

export enum RadialWheelTestIds {
  Component = "radial-wheel",
}

/**
 * Given a target element and the mouse's horizontal position, calculates
 * the tooltip placement for the target element (as if said element was divided in two halves by a vertical line).
 *
 * Criteria to determine the tooltip placement:
 * - `left` if the cursor is placed within the first half of the target element
 * - `right` if the cursor is placed within the second half of the target element
 *
 * @param mouseXPosition X coordinate of the mouse (horizontal position)
 * @param target Target DOM element
 * @returns The tooltip's placement, based on `mouseXPosition` and `target`.
 */
const calculateTooltipPlacement = (
  mouseXPosition: number,
  target: HTMLElement
): TooltipPlacement => {
  const rect = target.getBoundingClientRect();
  const x = mouseXPosition - rect.left; // x position within the element
  const relativeDistanceFromLeftSide = x / rect.width; // between 0 and 1 => 0: cursor completely at left, 1: cursor completely at right
  return relativeDistanceFromLeftSide < 0.5 ? "left" : "right"; // 0.5 is the center of the target element (50%)
};

const Container = styled.div`
  text-align: center;
  align-items: center;
`;

const GraphContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const Title = styled.div`
  text-align: center;
  margin-bottom: 24px;
  ${primaryActive.subtitle3};
  color: var(--color-primary-900);
`;

type RadialWheelProps = {
  id?: string;
  height?: number;
  className?: string;
  testId?: string;
  targetValue: number;
  currentValue: number;
  title?: string;
  legendLabel: string; // Custom label for the data legend displayed inside the graph
  quantityLabel?: string; // Custom label for the data quantity displayed inside the graph
  baseWheelTooltip?: string; // Tooltip text that will be displayed on hover over the "base wheel"
  progressWheelTooltip?: string; // Tooltip text that will be displayed on hover over the "progress wheel"
  isLoading?: boolean;
};

export const RadialWheel: FC<RadialWheelProps> = ({
  id,
  height = 132,
  className,
  testId,
  targetValue,
  currentValue,
  title,
  legendLabel,
  quantityLabel = `${currentValue - targetValue}`,
  baseWheelTooltip,
  progressWheelTooltip,
  isLoading,
}) => {
  const [showBaseWheelTooltip, setShowBaseWheelTooltip] =
    useState<boolean>(false);
  const [tooltipPlacement, setTooltipPlacement] =
    useState<TooltipPlacement>("right");

  const tooltipContent = useMemo(
    () => (showBaseWheelTooltip ? baseWheelTooltip : progressWheelTooltip),
    [showBaseWheelTooltip, baseWheelTooltip, progressWheelTooltip]
  );

  // Avoid dividing by 0
  const percent = targetValue === 0 ? 0 : (currentValue / targetValue) * 100;

  const handleHoverGraph =
    ({ isBaseWheel }: { isBaseWheel: boolean }): MouseEventHandler =>
    (event) => {
      setShowBaseWheelTooltip(isBaseWheel);
      setTooltipPlacement(
        calculateTooltipPlacement(event.clientX, event.target as HTMLElement)
      );
    };

  const GraphComponent = useCallback(
    () => (
      <Graph
        height={height}
        percent={percent}
        onHoverBaseWheel={handleHoverGraph({ isBaseWheel: true })}
        onHoverProgressWheel={handleHoverGraph({ isBaseWheel: false })}
        showProgress={!isLoading}
      />
    ),
    [height, percent, isLoading]
  );

  return (
    <Container
      id={id}
      className={className}
      data-testid={testId ?? RadialWheelTestIds.Component}
    >
      {title && <Title>{title}</Title>}
      <GraphContainer>
        {!isLoading && !!tooltipContent ? (
          <Tooltip content={tooltipContent} defaultPlacement={tooltipPlacement}>
            {<GraphComponent />}
          </Tooltip>
        ) : (
          <GraphComponent />
        )}
        <Data
          quantity={quantityLabel}
          legend={legendLabel}
          isLoading={isLoading}
        />
      </GraphContainer>
    </Container>
  );
};
